import {Injectable} from '@angular/core';
import {BehaviorSubject, forkJoin, Observable} from 'rxjs';
import {
  Company, ContainerTypes, Country,
  Currency, CurrencyExchange, DesconsolidationType, DocumentType, Equipment, EquipmentType, EventType, FeeType,
  Flow,
  FlowService,
  GetParams, Good, LoadUnit,
  Location,
  LocationType, Motonave, PackageType, Partner, PartnerType, ServiceOrder, ServiceType, TypeAnalyticService, Shipping, State,
  TransportType,
  User, UserRole
} from '@app/shared';
import {
  CompanyService,
  CurrencyService, DeconsolidationTypeService,
  DocumentService, EquipmentService, FeeService,
  FlowEventService, GoodService, LoadUnitService,
  LocationTransportService, PartnerService, StateService, StServiceService,
  UserService
} from '@app/core';
import {MotonaveService} from './motonave.service';
import {ShippingService} from './shipping.service';

@Injectable({
  providedIn: 'root'
})
export class GlobalValuesService {

  currencies$ = new BehaviorSubject<Currency[]>([])
  currenciesExchange$ = new BehaviorSubject<CurrencyExchange[]>([])
  flows$= new BehaviorSubject<Flow[]>([])
  places$ = new BehaviorSubject<Location[]>([])
  transportTypes$ = new BehaviorSubject<TransportType[]>([])
  users$ = new BehaviorSubject<User[]>([])
  userRoles$=new BehaviorSubject<UserRole[]>([])
  locationsTypes$ = new BehaviorSubject<LocationType[]>([])
  companies$ = new BehaviorSubject<Company[]>([])
  documentTypes$ = new BehaviorSubject<DocumentType[]>([])
  countries$ = new BehaviorSubject<Country[]>([])
  feeTypes$ = new BehaviorSubject<FeeType[]>([])
  equipmentTypes$ = new BehaviorSubject<EquipmentType[]>([])
  states$ = new BehaviorSubject<State[]>([])
  eventTypes$ = new BehaviorSubject<EventType[]>([])
  partnerTypes$ =new BehaviorSubject<PartnerType[]>([])
  motonaves$ =new BehaviorSubject<Motonave[]>([])
  packageTypes$ =new BehaviorSubject<PackageType[]>([])
  goods$ =new BehaviorSubject<Good[]>([])
  serviceTypes$ = new BehaviorSubject<ServiceType[]>([]);
  typeAnalyticServices$ = new BehaviorSubject<TypeAnalyticService[]>([]);
  shipping$ = new BehaviorSubject<Shipping[]>([]);
  equipments$ = new BehaviorSubject<Equipment[]>([]);

  isPartnerReady$= new BehaviorSubject<boolean>(false)

  partnerlist: Partner[] = [];
  mojitoEmployeeList: Partner[] = [];
  cecEmployeeList: Partner[] = [];
  clientsList: Partner[] = [];
  driversList: Partner[] = [];
  forwarderList: Partner[] = [];
  providerList: Partner[] = [];
  clientAndProviderList: Partner[] = [];
  supplierList: Partner[] = [];
  origenContactList: Partner[] = [];
  destinyContactList: Partner[] = [];
  shipperList: Partner[] = [];
  consigneeList: Partner[] = [];

  deconsolidationTypes: DesconsolidationType[] = [];
  loadUnits: LoadUnit[] = [];

  containerTypes$ =new BehaviorSubject<ContainerTypes[]>([]);

  _isFlowDataReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  get isFlowDataReady(): Observable<boolean> {
    return this._isFlowDataReady as Observable<boolean>
  }

  constructor(
    private currencyService: CurrencyService,
    private flowEventService: FlowEventService,
    private locationTransportService: LocationTransportService,
    private userService:UserService,
    private companyService:CompanyService,
    private documentService:DocumentService,
    private feeService:FeeService,
    private equipmentService:EquipmentService,
    private stateService:StateService,
    private partnerService:PartnerService,
    private deconsolidationTypeService: DeconsolidationTypeService,
    private loadUnitService: LoadUnitService,
    private stServiceService: StServiceService,
    private motonaveService: MotonaveService,
    private goodService: GoodService,
    private shippingService:ShippingService,
  ) {
    const params: GetParams = {
      startRow: 0,
      endRow: 5000,
      sortDirection: 'asc',
      sortProperty: 'id'
    }
    const flowParams: GetParams = {
      ...params,
      filterProperty: 'isNative',
      filterValue: 'true',
    }

    const request = {
      currencies: currencyService.getCurrency(params),
      currenciesExchange: currencyService.getCurrencyExchange(params),
      flows: flowEventService.getFlow(flowParams),
      places: locationTransportService.getLocation(params),
      transportTypes: locationTransportService.getTransportType(params),
      users: userService.getUsers(params),
      userRoles: userService.getUserRoles(params),
      locationsTypes: locationTransportService.getLocationType(params),
      companies: companyService.getCompany(params),
      documentTypes: documentService.getDocumentType(params),
      countries: locationTransportService.getCountry(params),
      feeTypes: feeService.getFeeType(params),
      equipmentTypes: equipmentService.getEquipmentType(params),
      states: stateService.getState(params),
      eventTypes: flowEventService.getEventType(params),
      partners: partnerService.getPartner(params),
      partnerType: partnerService.getPartnerType(params),
      deconsolidationTypes: deconsolidationTypeService.getDesconsolidationType(params),
      loadUnitService: loadUnitService.getLoadUnit(params),
      equipments: equipmentService.getEquipment(params),
      containerTypes: stServiceService.getContainerTypes(),
      serviceTypes: stServiceService.getServiceType(params),
      typeAnalyticServices: stServiceService.getTypeAnalyticService(params),
      motonaves: motonaveService.getMotonave(params),
      packageTypes: loadUnitService.getPackageType(params),
      goods: goodService.getGood(params),
      shipping: shippingService.getShipping(params),
    }

    forkJoin(request).subscribe({
      next: ({currencies, currenciesExchange, flows, places, transportTypes,
               users, userRoles, locationsTypes, companies, documentTypes,
             countries, feeTypes, equipmentTypes, states, eventTypes, partners, partnerType,
               deconsolidationTypes, loadUnitService, equipments, containerTypes, serviceTypes, typeAnalyticServices, motonaves,
               packageTypes, goods, shipping}) => {
        this.currencies$.next(currencies.data.rows);
        this.currenciesExchange$.next(currenciesExchange.data.rows);
        this.flows$.next(flows.data.rows);
        this.places$.next(places.data.rows);
        this.transportTypes$.next(transportTypes.data.rows)
        this.users$.next(users.data.rows);
        this.userRoles$.next(userRoles.data.rows);
        this.locationsTypes$.next(locationsTypes.data.rows);
        this.companies$.next(companies.data.rows);
        this.documentTypes$.next(documentTypes.data.rows);
        this.countries$.next(countries.data.rows);
        this.feeTypes$.next(feeTypes.data.rows);
        this.equipmentTypes$.next(equipmentTypes.data.rows);
        this.states$.next(states.data.rows);
        this.eventTypes$.next(eventTypes.data.rows);
        this.partnerTypes$.next(partnerType.data.rows);
        this.equipments$.next(equipments.data.rows);
        this.containerTypes$.next(containerTypes.data.rows);
        this.serviceTypes$.next(serviceTypes.data.rows);
        this.typeAnalyticServices$.next(typeAnalyticServices.data.rows);
        this.motonaves$.next(motonaves.data.rows);
        this.packageTypes$.next(packageTypes.data.rows);
        this.goods$.next(goods.data.rows);
        this.shipping$.next(shipping.data.rows);

        this.partner = partners.data.rows;

        this.setEventFlow(this.flows$.value);

        this.deconsolidationTypes = deconsolidationTypes.data.rows;
        //this.loadUnits = loadUnitService.data.rows;
      },
      error: (e) => console.error(e),
    })
    //getUserRoles don't return a httpRespond
    // this.userService.getUserRoles(params).subscribe({
    //   next: (roles) => this.userRoles$.next(roles),
    //   error: (e) => console.error(e),
    // })


  }

  setEventFlow(flows: Flow[]) {
    flows.forEach(flow => {
      flow.events?.forEach(event => {
        // Configuracion de los estados especiales
        if (event.name === 'Orden creada') {
          event.activator = {
            name: 'Automático',
            value: undefined,
            location: event.location || undefined,
          }
          event.icon = 'playlist_add_check';
          event.color = '#FF990A'
          return;
        }

        const dynamicField = this.flowEventService.mojitoDynamicFieldOptions.find(i => i.enum === event.mojitoDynamicField)

        if (event.name === 'Orden finalizada') {
          event.activator = {
            name: 'Manual',
            value: undefined,
            location: event.location || undefined,
            mojitoDynamicField: dynamicField,
          }
          event.icon = 'check_circle';
          event.color = '#45A557'
          return;
        }

        if (event.eventTriggers && event.eventTriggers.length > 0) {
          let name: 'Subida documento' | 'Fecha Mojito' | 'Validacion documento' | 'Manual' = 'Manual';
          let value: any;
          const location: Location | undefined = event.location;

          // La respuesta de edit-with-orders trae solo los ids (eventTriggerType y documentType a null):
          // sin este respaldo una etapa de subida de documento se leía como Manual.
          const trigger = event.eventTriggers[0];
          const triggerTypeId = trigger?.eventTriggerType?.id ?? trigger?.eventTriggerTypeId;
          const documentTypeId = trigger?.documentType?.id ?? trigger?.documentTypeId;

          if (triggerTypeId === 1) {
            name = 'Subida documento';
            value = documentTypeId || '-';
          } else if (triggerTypeId === 3) {
            name = 'Fecha Mojito';
            value = trigger.integrationField;
          } else if (triggerTypeId === 4) {
            name = 'Validacion documento';
            value = documentTypeId || '-';
          }

          event.activator = {
            name,
            value,
            location,
            mojitoDynamicField: this.flowEventService.mojitoDynamicFieldOptions.find(i => i.enum === event.mojitoDynamicField),
          }
        } else {
          event.activator = {
            name: 'Manual',
            value: undefined,
            location: event.location || undefined,
            mojitoDynamicField: dynamicField,
          }
        }
      })

      this._isFlowDataReady.next(true);
    })
  }

  set partner(list: Partner[]) {
    this.isPartnerReady$.next(false);

    this.partnerlist = list.sort((a, b) => {
      // Primero por nombre
      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;

      // Si los nombres son iguales, ordenar por apellido
      return (a.surName ?? "").localeCompare(b.surName);
    });

    this.mojitoEmployeeList = this.partnerlist.filter(p => p.partnerTypeId === 1);
    this.cecEmployeeList = this.partnerlist.filter(p => p.partnerTypeId === 2);
    this.clientsList = this.partnerlist.filter(p => p.partnerTypeId === 3);
    this.driversList = this.partnerlist.filter(p => p.partnerTypeId === 4);
    this.forwarderList = this.partnerlist.filter(p => p.partnerTypeId === 5);
    this.providerList = this.partnerlist.filter(p => p.partnerTypeId === 6||p.partnerTypeId===7);
    this.clientAndProviderList = this.partnerlist.filter(p => p.partnerTypeId === 7);
    this.origenContactList = this.partnerlist.filter(p => p.isCompany===false);
    this.destinyContactList = this.partnerlist.filter(p => p.isCompany===false);
    this.shipperList = this.partnerlist.filter(p => (p.partnerTypeId === 3||p.partnerTypeId===7) && p.isCompany===true);
    this.consigneeList = this.partnerlist.filter(p => p.partnerTypeId === 3||p.partnerTypeId===7);

    this.isPartnerReady$.next(true);
  }

  getServiceType(id: number): ServiceType | undefined {
    if (this.serviceTypes$.value && this.serviceTypes$.value.length > 0) {
      return this.serviceTypes$.value.find(st => st.id === id);
    } else return undefined
  }

  getTypeAnalyticService(id: number): TypeAnalyticService | undefined {
    if (this.typeAnalyticServices$.value && this.typeAnalyticServices$.value.length > 0) {
      return this.typeAnalyticServices$.value.find(tas => tas.id === id);
    } else return undefined
  }
}
